import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { ArrowLeft, ArrowRight, ArrowUpRight } from "lucide-react";
import { Link } from "react-router-dom";
import Reveal from "@/components/Reveal";
import { articles, readingSeries } from "@/data/siteData";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const baseSeriesArticles = readingSeries.slugs.map((slug) => articles.find((article) => article.slug === slug)).filter(Boolean);

function usePublishedSequenceSlot() {
  const [slot, setSlot] = useState(null);

  useEffect(() => {
    api.get(`${API}/reading-sequence`).then(({ data }) => setSlot(data.slot_5 || null)).catch(() => setSlot(null));
  }, []);

  return slot;
}

function seriesEntriesFor(slot) {
  const candidate = slot?.candidate_note && articles.find((article) => article.slug === slot.candidate_note);
  if (!slot) return baseSeriesArticles;
  if (candidate && !baseSeriesArticles.some((article) => article.slug === candidate.slug)) return [...baseSeriesArticles, candidate];
  if (candidate) return baseSeriesArticles;
  return [...baseSeriesArticles, { slug: "", title: slot.slot_title, isVirtual: true }];
}

export default function ReadingSeries() {
  const slot = usePublishedSequenceSlot();
  const seriesArticles = seriesEntriesFor(slot);
  const expanded = seriesArticles.length > baseSeriesArticles.length;

  return (
    <section className="reading-series" data-testid="reading-series">
      <div className="reading-series-head"><div><div className="section-kicker">{expanded ? "Five-note sequence / 02" : "Four-note sequence / 02"}</div><h2>{expanded ? "The next note in the sequence" : readingSeries.title}</h2></div><p>{readingSeries.intro}</p></div>
      <div className="reading-series-grid">{seriesArticles.map((article, index) => <Reveal key={article.slug || "slot-05"} delay={index * 65}>{article.isVirtual ? <div className="reading-series-card is-virtual" data-testid="reading-series-card-slot-5"><span className="card-index">{String(index + 1).padStart(2, "0")} / published slot</span><h3>{article.title}</h3><span className="reading-series-link">Approved sequence note</span></div> : <Link to={`/insights/${article.slug}`} className="reading-series-card" data-testid={`reading-series-card-${article.slug}`}><span className="card-index">{String(index + 1).padStart(2, "0")} / note</span><h3>{article.title}</h3><span className="reading-series-link">Read the note <ArrowUpRight size={13} /></span></Link>}</Reveal>)}</div>
      <div className="reading-series-question"><span>Next question</span><strong>{slot?.slot_question || readingSeries.nextQuestion}</strong></div>
    </section>
  );
}

export function ReadingSequenceNav({ slug }) {
  const slot = usePublishedSequenceSlot();
  const seriesArticles = seriesEntriesFor(slot);
  const position = readingSeries.slugs.indexOf(slug) !== -1 ? readingSeries.slugs.indexOf(slug) : seriesArticles.findIndex((article) => article.slug === slug);
  if (position === -1) return null;
  const previous = seriesArticles[position - 1];
  const next = seriesArticles[position + 1]?.slug ? seriesArticles[position + 1] : null;
  return <div className="reading-sequence-nav" data-testid={`reading-sequence-nav-${slug}`}><div><span className="section-kicker">Sequence / {String(position + 1).padStart(2, "0")} of {String(seriesArticles.length).padStart(2, "0")}</span><strong>{slot?.slot_question || readingSeries.nextQuestion}</strong></div><div className="reading-sequence-actions">{previous ? <Link to={`/insights/${previous.slug}`} data-testid={`reading-sequence-previous-${slug}`}><ArrowLeft size={13} /> Previous</Link> : <span />}{next ? <Link to={`/insights/${next.slug}`} data-testid={`reading-sequence-next-${slug}`}>Next question <ArrowRight size={13} /></Link> : <Link to="/insights" state={{ restoreInsights: true }} data-testid={`reading-sequence-finish-${slug}`}>Back to insights <ArrowUpRight size={13} /></Link>}</div></div>;
}
